'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { FC, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { sendForm } from '@/app/actions/send-form';
import Input from '@/components/shared/Fields/input';
import Label from '@/components/shared/Fields/label';
import Radio from '@/components/shared/Fields/radio';
import CustomDropdown from '@/components/shared/Fields/select';
import Textarea from '@/components/shared/Fields/textarea';
import { budgetList, FormPlacementValue, servicesList } from '@/components/shared/Footer/constants';
import Honeypot, { HoneypotHandle } from '@/components/shared/Honeypot';
import HorizontalFadeScroller from '@/components/shared/HorizontalFadeScroller';
import { Typography } from '@/components/shared/Typography';
import { ContactFormData, contactFormSchema } from '@/constants/form';
import { howYouFindQreamOptions, HowYouFindQreamValue } from '@/constants/how-you-find';
import routes from '@/constants/routes';
import { toastErrorStyles, toastSuccessStyles } from '@/constants/toast';
import { useModal } from '@/hooks/useModal';
import { clearUtmData, getUtmData } from '@/hooks/useUtmTracking';
import css from './style.module.scss';
interface Props {
  isModal?: boolean;
}
const FooterForm: FC<Props> = ({
  isModal
}) => {
  const {
    control,
    handleSubmit,
    formState: {
      errors,
      isSubmitting,
      isValid
    },
    reset
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange'
  });
  const howYouFindQreamValue = useWatch({
    control,
    name: 'howYouFindQream'
  });
  const {
    closeModal
  } = useModal();
  const honeypotRef = useRef<HoneypotHandle>(null);
  const pathname = usePathname();
  const wrapperRef = useRef<HTMLFormElement | null>(null);
  const onSubmit = async (data: ContactFormData) => {
    if (honeypotRef.current?.isFilled()) {
      reset();
      if (isModal) closeModal();
      toast.success('Form submitted successfully', toastSuccessStyles);
      return;
    }
    const updatedData = Object.fromEntries(Object.entries(data).map(([key, value]) => {
      if (Array.isArray(value)) return [key, value.join(', ')];
      return [key, value];
    }));
    updatedData.sentFromPage = pathname === '/' ? 'Home Page' : pathname;
    updatedData.formPlacement = isModal ? FormPlacementValue.Modal : FormPlacementValue.Footer;
    const utmData = getUtmData();
    Object.assign(updatedData, utmData);
    try {
      await sendForm('/contact-forms', updatedData as any);
      clearUtmData();
      reset();
      if (isModal) closeModal();
      toast.success('Form submitted successfully', toastSuccessStyles);
    } catch (error) {
      toast.error('Error submitting form', toastErrorStyles);
    }
  };
  return <form ref={wrapperRef} className={css.form} onSubmit={handleSubmit(onSubmit)}>
			<Honeypot ref={honeypotRef} />

			<div className={css.heading}>
				<p className={css.title}>Not into calls?</p>

				<Typography variant="body-s" tag="p" className={css.description}>
					No stress. Drop your details below and we’ll take it from there.
				</Typography>
			</div>

			<Label text="Service" />
			<HorizontalFadeScroller outerClassName={css.scrollOuter} innerClassName={css.fadeScrollerInner}>
				<div className={css.radioWrap}>
					<Controller name="service" control={control} defaultValue={[]} render={({
          field
        }) => <Radio multiple selectedValue={field.value} options={servicesList} onChange={field.onChange} />} />
				</div>

				{errors.service?.message ? <p className={css.error}>{errors.service.message}</p> : null}
			</HorizontalFadeScroller>

			<Label text="Budget" />
			<HorizontalFadeScroller outerClassName={css.scrollOuter} innerClassName={css.fadeScrollerInner}>
				<div className={css.radioWrap}>
					<Controller name="budget" control={control} defaultValue={undefined} render={({
          field: field_0
        }) => <Radio selectedValue={field_0.value} options={budgetList} onChange={field_0.onChange} />} />
				</div>

				{errors.budget?.message ? <p className={css.error}>{errors.budget.message}</p> : null}
			</HorizontalFadeScroller>

			<div className={css.fieldsWrap}>
				<div className={css.field}>
					<Controller name="name" control={control} defaultValue="" render={({
          field: field_1
        }) => <Input allowedPattern={/^[A-Za-zА-Яа-яЁёІіЇїЄєҐґ\s'-]*$/} {...field_1} placeholder="Your name" />} />

					{errors.name?.message ? <p className={css.error}>{errors.name.message}</p> : null}
				</div>

				<div className={css.field}>
					<Controller name="email" control={control} defaultValue="" render={({
          field: field_2
        }) => <Input {...field_2} placeholder="Your email" />} />
					{errors.email?.message ? <p className={css.error}>{errors.email.message}</p> : null}
				</div>

				<div className={css.field}>
					<Controller name="jobTitle" control={control} defaultValue="" render={({
          field: field_3
        }) => <Input {...field_3} placeholder="Job title" />} />
				</div>

				<div className={css.field}>
					<Controller name="howYouFindQream" control={control} defaultValue="" render={({
          field: field_4
        }) => <CustomDropdown {...field_4} value={howYouFindQreamOptions.find(option => option.value === field_4.value) || null} options={howYouFindQreamOptions} />} />
				</div>

				{howYouFindQreamValue === HowYouFindQreamValue.Other && <div className={clsx(css.field, css.fullWidthField)}>
						<Controller name="howYouFindQreamOther" control={control} defaultValue="" render={({
          field: field_5
        }) => <Input {...field_5} placeholder="Let us know where you heard about us" />} />
						{errors.howYouFindQreamOther?.message ? <p className={css.error}>{errors.howYouFindQreamOther.message}</p> : null}
					</div>}

				<div className={clsx(css.field, css.fullWidthField)}>
					<Controller name="details" control={control} defaultValue="" render={({
          field: field_6
        }) => <Textarea {...field_6} placeholder="Describe your project" />} />
					{errors.details?.message ? <p className={css.error}>{errors.details.message}</p> : null}
				</div>
			</div>

			<div className={css.footerBottom}>
				<p className={css.footerDescription}>
					<span>Curious how we handle your data with care? Scoop into our</span>
					&nbsp;
					<a href={routes.privacyPolicy} target="_blank" rel="noreferrer">
						Privacy Policy
					</a>
				</p>

				<button disabled={isSubmitting} type="submit" className={clsx(css.submit, {
        [css.active]: isValid && !isSubmitting
      })}>
					Submit
				</button>
			</div>
		</form>;
};
export default FooterForm;